// 009: every string of the cart, payment and confirmation screens (spec Clarifications, FR-010..FR-045).
import type { AppError } from "@/api/client";

export const CHECKOUT_COPY = {
  title: "Cart",
  steps: ["Cart", "Address & payment", "Confirmation"] as const,
  loadFailed: "Couldn't load your cart. Check your connection and try again.",
  retry: "Try again",
  empty: "Your cart is empty",
  emptyAction: "Shop School Books",
  remove: "Remove",
  unavailable: "Not available",
  items: (n: number) => `${n} ${n === 1 ? "item" : "items"}`,
  summary: "Order summary",
  itemsTotal: "Items",
  delivery: "Delivery",
  free: "Free",
  total: "Total",
  split: (n: number) =>
    `Your cart has items from ${n} schools, so it will be ${n} orders, paid together. Each school's order is delivered separately.`,
  deliverTo: "Deliver to",
  change: "Change",
  addAddress: "Add address",
  pinSaved: "Map pin saved",
  noAddress: "Add a delivery address with a map pin to pay",
  chooseAddress: "Choose a delivery address",
  mobileTitle: "Mobile number",
  mobileNeeded: "Add your mobile number so the delivery partner can call you",
  mobileSave: "Save number",
  mobileInvalid: "Enter a 10-digit Indian mobile number.",
  payment: "Payment method",
  methods: {
    UPI: { label: "UPI", helper: "Google Pay, PhonePe, Paytm or any UPI app" },
    CARD: { label: "Debit / credit card", helper: "Visa, Mastercard, RuPay" },
    NETBANKING: { label: "Net banking", helper: "All major Indian banks" },
  },
  pay: (total: string) => `Pay ${total}`,
  secure: (provider: string | null) =>
    provider ? `Secure payment through ${provider}` : "Secure payment",
  blocked: {
    empty: "Your cart is empty.",
    unavailable: "Remove the items that aren't available to pay.",
    noAddress: "Add a delivery address with a map pin to pay.",
    noMobile: "Add your mobile number to pay.",
    offline: "You're offline.",
    busy: "Your payment is in progress.",
  },
  confirming: "Confirming your payment…",
  stillConfirming:
    "We're still confirming your payment. You'll find your order in My orders once it's confirmed.",
  underReview: "Your payment is being reviewed by Akaiva. We'll contact you.",
  expired: "This payment session expired. Your cart is unchanged — try again.",
  superseded:
    "A newer payment replaced this one. Check My orders before paying again.",
  failed: "The payment didn't go through. Your cart is unchanged.",
  cancelled: "Payment cancelled. Your cart is unchanged.",
  goToOrders: "Go to My orders",
  // Confirmation (FR-045).
  confirmedTitle: "Order placed",
  confirmedLead: (n: number) =>
    n === 1 ? "Your order is placed." : `Your ${n} orders are placed.`,
  paid: "Paid",
  trackOrder: "Track order",
  continueShopping: "Continue shopping",
  // The fake gateway sheet (local/staging only).
  fakeTitle: "Test payment",
  fakeLead: "This build uses a test gateway. No money moves.",
  fakeSuccess: "Pay successfully",
  fakeFail: "Fail payment",
  fakeCancel: "Cancel",
  noGateway:
    "Payments aren't available in this app build yet. Pay on app.akaiva-edu.com.",
} as const;

const MESSAGES: Record<string, string> = {
  PRICE_CHANGED:
    "Prices changed since you opened your cart. Check the new total and pay again.",
  CART_EMPTY: "Your cart is empty.",
  CART_HAS_UNAVAILABLE_ITEMS:
    "Some items aren't available any more. Remove them to pay.",
  SCHOOL_NOT_ACTIVE: "A school in your cart isn't taking orders right now.",
  MOBILE_REQUIRED:
    "Add your mobile number so the delivery partner can call you.",
  PAYMENT_PROVIDER_UNAVAILABLE:
    "Payments are unavailable right now. Please try again in a few minutes.",
  PAYMENT_VERIFICATION_FAILED:
    "We couldn't verify this payment. If money was taken, it will be refunded.",
  RATE_LIMITED: "Too many attempts. Please wait a minute and try again.",
};

export function checkoutMessage(error: AppError): {
  text: string;
  reference: string | null;
} {
  if (error.kind === "offline" || error.kind === "timeout")
    return { text: CHECKOUT_COPY.blocked.offline, reference: null };
  return {
    text:
      (error.code && MESSAGES[error.code]) ||
      "Something went wrong. Please try again.",
    reference: error.requestId,
  };
}
